import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Clock, ChevronLeft, ArrowRight } from "lucide-react";
import Seo from "@/components/common/Seo";
import Reveal from "@/components/common/Reveal";
import NotFound from "@/pages/NotFound";
import api from "@/lib/api";
import { formatINR } from "@/lib/format";
import { getGuide, GUIDES } from "@/data/guides";

export default function GuideDetail() {
  const { slug } = useParams();
  const guide = getGuide(slug);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (!guide) return;
    let cancelled = false;
    Promise.all(guide.relatedProducts.map((s) => api.getProduct(s).catch(() => null)))
      .then((res) => { if (!cancelled) setRelated(res.filter(Boolean)); });
    return () => { cancelled = true; };
  }, [guide]);

  if (!guide) return <NotFound />;

  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo title={guide.title} description={guide.excerpt} />

      <article className="acx-container px-4 py-12 sm:px-6 lg:px-10">
        <Link to="/guides" className="inline-flex items-center gap-1.5 text-[13px] text-white/55 transition-colors hover:text-white">
          <ChevronLeft size={16} /> All guides
        </Link>

        <Reveal>
          <div className="mt-5 max-w-3xl">
            <div className="flex items-center gap-3 text-[12px] text-white/50">
              <span className="rounded-full bg-[#E11B22]/15 px-2.5 py-1 font-semibold text-[#E11B22]">{guide.category}</span>
              <span className="inline-flex items-center gap-1"><Clock size={12} /> {guide.readTime}</span>
            </div>
            <h1 className="mt-4 font-display text-[34px] font-extrabold leading-[1.08] tracking-[-0.02em] sm:text-5xl">{guide.title}</h1>
            <p className="mt-4 text-[17px] leading-relaxed text-white/65">{guide.excerpt}</p>
          </div>
        </Reveal>

        <div className="mt-8 overflow-hidden rounded-[18px] border border-[color:var(--acx-border)]">
          <img src={guide.cover} alt={guide.title} className="max-h-[420px] w-full object-cover" />
        </div>

        <div className="mt-10 max-w-3xl space-y-8" data-testid="guide-body">
          {guide.sections.map((s, i) => (
            <section key={s.heading || i}>
              <h2 className="font-display text-[24px] font-bold tracking-[-0.01em]">{s.heading}</h2>
              <p className="mt-3 text-[16px] leading-relaxed text-white/70">{s.body}</p>
            </section>
          ))}
        </div>

        {/* Internally linked products */}
        {related.length > 0 && (
          <div className="mt-14 border-t border-[color:var(--acx-border)] pt-10">
            <h2 className="font-display text-2xl font-bold tracking-[-0.01em]">Products mentioned in this guide</h2>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="guide-related-products">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/product/${p.slug}`}
                  className="group rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-3 transition-colors hover:border-white/20"
                >
                  <div className="aspect-square overflow-hidden rounded-[10px] bg-[var(--acx-bg-2)]">
                    <img src={p.images?.[0]} alt={p.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  </div>
                  <p className="mt-3 line-clamp-2 text-[13px] font-medium text-white">{p.name}</p>
                  <p className="mt-1 text-[14px] font-semibold text-white/80">{formatINR(p.price)}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* More guides */}
        <div className="mt-14 border-t border-[color:var(--acx-border)] pt-10">
          <h2 className="font-display text-2xl font-bold tracking-[-0.01em]">More guides</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {GUIDES.filter((g) => g.slug !== guide.slug).slice(0, 3).map((g) => (
              <Link key={g.slug} to={`/guides/${g.slug}`} className="group flex items-center justify-between gap-3 rounded-[14px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-4 transition-colors hover:border-white/20">
                <span className="text-[14px] font-medium text-white/85">{g.title}</span>
                <ArrowRight size={16} className="shrink-0 text-white/40 transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}
